import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, MenuItem, Stack, Typography } from '@mui/material';
import { userModel } from 'entities/user';

import {
  BORDER_RADIUS_L,
  BORDER_RADIUS_M,
  chains,
  checkRequestStatusEquality,
  COLOR_NEUTRAL_ULTRALIGHT,
  COLOR_WH,
  ContractsNames,
  ContractsService,
  flexHelper,
  FontWeights,
  fromDecimals,
  Icon,
  NATIVE_CURRENCY_ADDRESS,
  nativeAddressesChecksum,
  RequestStatus,
  Spinner,
  uiSelector,
  useShallowSelector,
  useValidateInputField,
  validateOnlyNumbers,
  ValidationTypes,
} from '../../shared';
import { TokenInput } from '../../features';
import MogulLogo from '../../shared/assets/images/mogul-logo.png';
import icons from '../../shared/ui/Icon/icons';
import { MenuItemsProps, Selector } from '../../shared/ui/Selector';
import BigNumber from 'bignumber.js';
import { useDispatch } from 'react-redux';
import { useWeb3Provider } from '../../shared/config/wallet-connect';
import { userActions } from '../../entities/user/model';

// const NATIVE_PRICE_DECIMALS = 18;

enum SelectorTokenType {
  Native = 'Native',
  USDT = 'USDT',
}

interface TokenToSelectProps {
  icon?: keyof typeof icons;
  symbol?: string;
}

const TokenSelect: React.FC<{
  tokens: Record<SelectorTokenType, TokenToSelectProps>;
  selectedTokenType: SelectorTokenType;
  setSelectedTokenType: (value: SelectorTokenType) => void;
}> = ({ tokens, selectedTokenType, setSelectedTokenType }) => {
  const MenuItems: React.FC<MenuItemsProps<SelectorTokenType>> = ({ onSelect }) =>
    Object.entries(tokens).map(([type, { icon, symbol }]) => (
      <MenuItem
        key={type}
        onClick={() => onSelect(type as SelectorTokenType)}
        sx={{
          background: 'transparent',
          padding: 1,
          '&:first-of-type': {
            borderTopLeftRadius: 'unset',
            borderTopRightRadius: 'unset',
          },
          '&:last-of-type': {
            borderBottomLeftRadius: 'unset',
            borderBottomRightRadius: 'unset',
          },
          '& + &': {
            borderTop: 'none',
          },
          borderRadius: '12px !important',
        }}
      >
        <Stack direction="row" alignItems="center" spacing="10px">
          {icon && <Icon type={icon} />}
          <Typography variant="button" fontWeight={FontWeights.Medium} lineHeight="19px">
            {symbol || '...'}
          </Typography>
        </Stack>
      </MenuItem>
    ));

  return (
    <Selector<SelectorTokenType>
      MenuItems={MenuItems}
      onSelect={setSelectedTokenType}
      selectedIcon={tokens?.[selectedTokenType]?.icon}
      selectedText={tokens?.[selectedTokenType]?.symbol}
      menuProps={{
        slotProps: {
          paper: {
            sx: {
              padding: 1,
              width: 120,
              borderRadius: '20px',
            },
          },
        },
      }}
    />
  );
};

const BuyPriceBlock = ({ priceAtUsdt }: { priceAtUsdt: '' | BigNumber }) => {
  return (
    <Stack
      spacing={1}
      alignItems="center"
      justifyContent="center"
      sx={(theme) => ({
        width: '100%',
        border: `1px solid ${theme.themeColors.colorTimerBorder}`,
        padding: '16px 0 36px 0',
        borderRadius: BORDER_RADIUS_L,
      })}
    >
      <Typography variant="button" fontWeight={FontWeights.Medium}>
        Price
      </Typography>
      <Typography fontSize={40}>${priceAtUsdt ? priceAtUsdt.toNumber() : 0}</Typography>
    </Stack>
  );
};

export const Buy = () => {
  const { chainType, network } = useShallowSelector(userModel.selectors.selectUserWeb3Info);

  const {
    price: contractPrice,
    nativePrice: contractNativePrice,
    tokensLeftOnContract,
  } = useShallowSelector(userModel.selectors.selectBuyInfo);

  const web3Provider = useWeb3Provider();
  const dispatch = useDispatch();

  const tokens = useShallowSelector(userModel.selectors.selectTokenBalances);
  const starsV2Token =
    tokens[ContractsService.getContractData(ContractsNames.StarsV2, network, chainType)?.address?.toLowerCase()];
  const usdtAddress = ContractsService.getContractData(ContractsNames.USDTToken, network, chainType)?.address;

  const usdtToken = tokens[usdtAddress?.toLowerCase()];
  const nativeCurrency = useMemo(
    () => ({
      ...tokens[NATIVE_CURRENCY_ADDRESS],
      symbol: chains[network][chainType].nativeCurrency.symbol,
    }),
    [tokens, network, chainType],
  );

  const tokensToSelect = useMemo(() => {
    const usdtDecimals = Number(usdtToken?.decimals);

    const priceAtUsdt = usdtToken?.decimals && BigNumber(contractPrice).div(10 ** usdtDecimals);
    const priceAtNative =
      contractPrice && priceAtUsdt && priceAtUsdt.div(BigNumber(contractNativePrice).div(10 ** usdtDecimals));

    return {
      [SelectorTokenType.USDT]: {
        ...usdtToken,
        icon: usdtToken?.symbol as keyof typeof icons,
        address: usdtAddress,
        price: priceAtUsdt,
      },
      [SelectorTokenType.Native]: {
        ...nativeCurrency,
        icon: nativeCurrency?.symbol as keyof typeof icons,
        address: nativeAddressesChecksum[network],
        price: priceAtNative,
      },
    };
  }, [network, usdtToken, nativeCurrency, contractPrice, contractNativePrice]);

  const [selectedTokenType, setSelectedTokenType] = useState<SelectorTokenType>(SelectorTokenType.USDT);
  const selectedToken = tokensToSelect[selectedTokenType];

  const getTokenPrice = useCallback(
    (tokenType: SelectorTokenType = selectedTokenType) => tokensToSelect[tokenType].price || BigNumber('0'),
    [tokensToSelect, selectedTokenType],
  );

  const [errorMessage, setErrorMessage] = useState('');
  const [contractErrorMessage, setContractErrorMessage] = useState('muhomor');
  const [amountFrom, handleChangeAmountFrom, setAmountFrom] = useValidateInputField({ type: ValidationTypes.number });
  const [amountTo, handleChangeAmountTo, setAmountTo] = useValidateInputField({ type: ValidationTypes.number });

  const uiState = useShallowSelector(uiSelector);
  const isBuying = checkRequestStatusEquality(uiState[userActions.buy?.type], RequestStatus.REQUEST);
  const isBuyComplete = checkRequestStatusEquality(
    uiState[userActions.buy?.type],
    RequestStatus.SUCCESS || RequestStatus.ERROR,
  );
  const isBuyDisabled = useMemo(
    () => !+amountFrom || !!errorMessage || !!contractErrorMessage,
    [amountFrom, errorMessage, contractErrorMessage, isBuying],
  );

  const resetInputs = useCallback(() => {
    setAmountFrom('');
    setAmountTo('');
  }, [setAmountFrom, setAmountTo]);

  const updateAmountTo = (value: string | number, tokenPrice: BigNumber) => {
    if (value && tokenPrice && validateOnlyNumbers({ value: `${value}` })) {
      setAmountTo(BigNumber(BigNumber(value).div(tokenPrice).toFixed(Number(starsV2Token?.decimals), 1)).toString(10));
    } else setAmountTo('');
  };

  const handleChangeFromInput = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    handleChangeAmountFrom(e);
    updateAmountTo(e.target.value, getTokenPrice());
  };

  const handleChangeToInput = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const currentPrice = getTokenPrice();
    handleChangeAmountTo(e);
    if (e.target.value && currentPrice && validateOnlyNumbers({ value: e.target.value })) {
      setAmountFrom(
        BigNumber(BigNumber(e.target.value).times(currentPrice).toFixed(Number(selectedToken?.decimals), 1)).toString(
          10,
        ),
      );
    } else setAmountFrom('');
  };

  const handleSelectedTokenTypeChange = (tokenType: SelectorTokenType) => {
    setSelectedTokenType(tokenType);
    resetInputs();
  };

  const handleMaxClick = () => {
    if (selectedToken?.balance && selectedToken?.decimals) {
      const value = BigNumber(selectedToken.balance)
        .div(10 ** +selectedToken.decimals)
        .toFixed();
      handleChangeFromInput({ target: { value } } as ChangeEvent<HTMLTextAreaElement | HTMLInputElement>);
    }
  };

  const handleBuy = useCallback(() => {
    dispatch(
      userModel.userActions.buy({
        web3Provider,
        token: selectedToken.address,
        amount: amountFrom,
        decimals: selectedToken.decimals,
      }),
    );
  }, [dispatch, amountFrom, selectedToken.address, selectedToken?.decimals, web3Provider]);

  useEffect(() => {
    if (
      BigNumber(amountFrom)
        .times(10 ** +selectedToken.decimals)
        .isGreaterThan(selectedToken?.balance || 0)
    )
      setErrorMessage('Insufficient balance');
    else setErrorMessage('');

    if (
      starsV2Token &&
      BigNumber(amountTo)
        .times(10 ** +starsV2Token.decimals)
        .isGreaterThan(tokensLeftOnContract || 0)
    ) {
      const tokensLeftOnContractNumber = fromDecimals(tokensLeftOnContract, starsV2Token.decimals, 2);
      setContractErrorMessage(`Not enough tokens for sale. Only ${tokensLeftOnContractNumber} Mogul are available`);
    } else setContractErrorMessage('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountFrom, selectedToken]);

  useEffect(() => {
    if (isBuyComplete) {
      resetInputs();
    }
  }, [isBuyComplete, setAmountFrom, setAmountTo]);

  return (
    <Card sx={{ maxWidth: '100%', width: 520, mt: 3 }}>
      <Stack spacing={2}>
        <Stack
          spacing={1}
          sx={{
            position: 'relative',
          }}
        >
          <TokenInput
            label="You pay"
            tokenComponent={
              <TokenSelect
                tokens={tokensToSelect}
                selectedTokenType={selectedTokenType}
                setSelectedTokenType={handleSelectedTokenTypeChange}
              />
            }
            tokenBalance={fromDecimals(selectedToken?.balance, selectedToken?.decimals, 2)}
            error={errorMessage}
            onMaxClick={handleMaxClick}
            value={amountFrom}
            onChange={handleChangeFromInput}
          />
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              width: '36px',
              height: '36px',
              background: COLOR_NEUTRAL_ULTRALIGHT,
              border: `4px solid ${COLOR_WH}`,
              borderRadius: BORDER_RADIUS_M,
              position: 'absolute',
              top: `88px`,
              left: 'calc(50% - 18px)',
              zIndex: 10,
              mt: '0 !important',
            }}
          >
            <Icon type="arrow" width={10} height={14} />
          </Stack>
          <TokenInput
            label="You receive"
            tokenIcon={<Box component="img" src={MogulLogo} width={32} height={32} />}
            tokenSymbol={starsV2Token?.symbol}
            value={amountTo}
            onChange={handleChangeToInput}
            error={contractErrorMessage}
          />
        </Stack>
        <BuyPriceBlock priceAtUsdt={tokensToSelect[SelectorTokenType.USDT].price} />
        <Button
          sx={{ width: '100%', mt: 2, '.MuiButton-endIcon': { width: 16, mr: '-8px', ml: 0 } }}
          {...(isBuying ? { endIcon: <Spinner iconSize={16} sx={{ ...flexHelper('center', 'center') }} /> } : {})}
          disabled={isBuyDisabled}
          onClick={handleBuy}
        >
          Buy
        </Button>
      </Stack>
    </Card>
  );
};
